import { useState } from 'react';
import * as PropTypes from 'prop-types';
import cx from 'classnames';
import { CheckIcon } from 'icons/solid';
import Image from '../../atoms/Image/Image';
import Accordion from '../../molecules/Accordion/Accordion';
import Link from '../../atoms/Link/Link';

const ProductFeature = ({
    wrapperClassName,
    accordionTittle,
    activeTab,
    children,
    linkText,
    handleToggle,
    onClickLink,
}) => {
    return (
        <div className={wrapperClassName}>
            <Accordion
                key='accordion'
                active={activeTab}
                title={accordionTittle}
                childrenClassName={cx(linkText && 'flex flex-col gap-y-2')}
                toggleAccordion={handleToggle}
                withBorder
            >
                {children}
                {linkText && typeof onClickLink === 'function' && (
                    <Link onClick={onClickLink}>{linkText}</Link>
                )}
            </Accordion>
        </div>
    );
};

const ProductContent = ({ facets }) => {
    return facets
        .filter((f) => f.isMain)
        .map((facet, index) => {
            return (
                <div className='flex' key={facet?.id || index}>
                    <CheckIcon className='mr-3 mt-1 h-4 w-4 rounded-full bg-black fill-white stroke-white stroke-2 p-1' />
                    <div>
                        {facet.name && facet.name !== '' && (
                            <>
                                <h4 className='mb-2.5 mt-0 font-heading text-normal text-ui-black'>
                                    {facet.name}
                                </h4>
                                <p className='font-body text-small'>{facet?.description}</p>
                            </>
                        )}
                    </div>
                </div>
            );
        });
};

const ProductIngredients = ({ ingredients, products, isBundle, PATH_PRODUCT_PAGE }) => (
    <ul className='list-inside list-disc'>
        {ingredients &&
            ingredients.length > 0 &&
            ingredients.map(({ id, name, breakdown }) => (
                <li key={id}>
                    {name} {breakdown && <span className='text-ui-gray-500'>({breakdown})</span>}
                </li>
            ))}
        {isBundle &&
            products.slice(0, 8).map((subproduct, index) => (
                <li key={'subproduct-' + index} className='mt-2 flex items-start'>
                    {subproduct.quantity && subproduct.quantity + 'x '}
                    <Link
                        className='font-light ml-2'
                        href={PATH_PRODUCT_PAGE + '/' + subproduct.slug}
                    >
                        {subproduct.name}
                    </Link>
                </li>
            ))}
    </ul>
);

const ProductDetail = ({ product, isMobile, modalContent, setModalContent, PATH_PRODUCT_PAGE }) => {
    const [activeTab, setActiveTab] = useState(0);

    const productImage =
        product.images && product.images.length
            ? (product.images || []).find((x) => x.isInCollapsedSection) ||
              product.images[product.images.length - 1]
            : null;

    const productDescriptionHTML = (
        <span
            className='font-body text-ui-black'
            dangerouslySetInnerHTML={{
                __html: product.longDescription ?? product.description,
            }}
        ></span>
    );

    const toggleTab = (number) => {
        if (isMobile)
            switch (number) {
                case 0:
                    setModalContent({
                        title: 'Acerca de: ',
                        content: productDescriptionHTML,
                        isOpen: true,
                    });
                    break;
                case 1:
                    setModalContent({
                        title: '¿Por qué lo amamos?',
                        content: <ProductContent facets={product.facets} />,
                        isOpen: true,
                    });
                    break;
                case 2:
                    setModalContent({
                        title: product.isBundle ? '¿Qué incluye?' : 'De qué está hecho',
                        content: (
                            <ProductIngredients
                                isBundle={product?.isBundle}
                                products={product?.products}
                                ingredients={product.ingredients}
                                PATH_PRODUCT_PAGE={PATH_PRODUCT_PAGE}
                            />
                        ),
                        isOpen: true,
                    });
                    break;
            }
        else {
            activeTab === number ? setActiveTab(null) : setActiveTab(number);
        }
    };

    return (
        <>
            <div
                style={{
                    backgroundColor: product?.backgroundColor || '#fdf2f1',
                }}
            >
                <div className='container flex w-full pb-2 lg:max-h-[607px]'>
                    <div className='mr-2 flex w-full flex-col overflow-auto lg:max-h-max lg:w-1/2'>
                        <ProductFeature
                            accordionTittle='Acerca de'
                            handleToggle={() => toggleTab(0)}
                            activeTab={activeTab === 0 && !isMobile}
                        >
                            {productDescriptionHTML}
                        </ProductFeature>
                        {product?.facets && product?.facets?.length > 0 && (
                            <ProductFeature
                                accordionTittle='¿Por qué lo amamos?'
                                handleToggle={() => toggleTab(1)}
                                activeTab={activeTab === 1 && !isMobile}
                            >
                                <ProductContent facets={product.facets} />
                            </ProductFeature>
                        )}
                        <ProductFeature
                            accordionTittle={
                                product.isBundle ? '¿Qué incluye?' : 'De qué está hecho'
                            }
                            handleToggle={() => toggleTab(2)}
                            activeTab={activeTab === 2 && !isMobile}
                        >
                            <ProductIngredients
                                ingredients={product?.ingredients}
                                isBundle={product?.isBundle}
                                products={product?.products}
                                PATH_PRODUCT_PAGE={PATH_PRODUCT_PAGE}
                            />
                        </ProductFeature>
                    </div>
                    <div className='relative hidden h-0 lg:block lg:h-[607px] lg:w-1/2'>
                        {productImage && (
                            <Image
                                src={productImage.url}
                                alt={productImage.alt ?? productImage.name}
                                layout='fill'
                                objectFit='cover'
                                objectPosition='center'
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

ProductDetail.propTypes = {
    product: PropTypes.object,
    isMobile: PropTypes.bool,
    modalContent: PropTypes.object,
    setModalContent: PropTypes.func,
    PATH_PRODUCT_PAGE: PropTypes.string,
};

export default ProductDetail;
